/**
 * Bringing a control into view by moving one box and no others.
 *
 * `scrollIntoView` is the obvious way to do this and the wrong one inside the
 * dashboard shell. It asks the browser to bring the element into view by
 * whatever scrolling that takes, and it takes it wherever it can: `overflow-
 * hidden` hides a scrollbar, it does not refuse to scroll. Every screen here
 * is laid out inside boxes that hide their overflow on purpose — the page
 * owns the viewport height from `lg` up and only its form column scrolls — so
 * the browser reaches past the column it was meant to move and shoves the page
 * itself up, carrying the heading, the tabs and the setup strip off the top of
 * the screen.
 *
 * There is no scrolling back from that. A box with `overflow: hidden` takes a
 * scroll offset happily and gives the reader no way to undo it: no scrollbar,
 * no wheel, and no re-clamp on the next reflow, so the page stays shoved up —
 * with a band of blank page below it where the content used to reach — until
 * the screen is reloaded.
 *
 * So the box that may move is chosen here rather than left to the browser, and
 * only a box that is a scroller in its own right is eligible. The same rule
 * `CustomDropdown` keeps by hand for its list, which is where this was learned.
 */

/**
 * The nearest ancestor that both scrolls and has somewhere to scroll to.
 *
 * `hidden` is deliberately not a scroller here, which is the whole point:
 * those are the boxes that must not move. `auto` and `scroll` are, and only
 * when there is actually overflow in them — a box whose content fits is not
 * the one that would have brought the control into view, and stopping at it
 * would leave the control where it was.
 */
const scrollerAround = (control: HTMLElement): HTMLElement | null => {
  for (let box = control.parentElement; box; box = box.parentElement) {
    const { overflowY } = window.getComputedStyle(box);
    const scrolls = overflowY === "auto" || overflowY === "scroll";
    if (scrolls && box.scrollHeight > box.clientHeight) return box;
  }
  return null;
};

/**
 * Scrolls `control` to the middle of whichever box is carrying it.
 *
 * Which box that is depends on the width, and neither is named here: from `lg`
 * up a long form scrolls its own sections and that column is the scroller;
 * below it the form grows and the shell's panel is. Walking up to the first
 * real scroller finds whichever it is without the form having to know which
 * screen it is on.
 *
 * Nothing happens if there is no scroller above it — a public form scrolls the
 * window, which has no overflow to find, and the window is the one scroller
 * `scrollIntoView` may safely be left to move.
 */
export const scrollWithin = (control: HTMLElement): void => {
  const box = scrollerAround(control);
  if (!box) return;

  const boxRect = box.getBoundingClientRect();
  const rect = control.getBoundingClientRect();

  // Centred in the box, the way `block: "center"` centred it — a field that
  // lands flush against the top or bottom edge of the column reads as the last
  // one rather than the next one. The browser clamps the ends for us.
  box.scrollTo({
    top:
      box.scrollTop +
      rect.top -
      boxRect.top -
      (boxRect.height - rect.height) / 2,
    behavior: "smooth",
  });
};

export default scrollWithin;
